import { HttpError } from '@/lib/https';

/**
 * Two different 400 shapes to normalize (see the API guide, §11):
 * - envelope: `errors: ["Name: Name is required."]` — array of "Field: reason" strings
 * - ProblemDetails (missing required field, e.g. `name` on create): `errors: { "Name": ["..."] }`
 */
export const parseTemplateFieldErrors = (
  errors: string[] | Record<string, string[]> | null | undefined
): Record<string, string[]> => {
  const fieldErrors: Record<string, string[]> = {};

  if (Array.isArray(errors)) {
    for (const entry of errors) {
      const i = entry.indexOf(': ');
      if (i < 0) {
        (fieldErrors._generic ??= []).push(entry);
        continue;
      }
      const field = entry.slice(0, i);
      const message = entry.slice(i + 2);
      (fieldErrors[field] ??= []).push(message);
    }
    return fieldErrors;
  }

  if (errors && typeof errors === 'object') {
    for (const [field, messages] of Object.entries(errors)) {
      fieldErrors[field] = messages;
    }
  }

  return fieldErrors;
};

export const TEMPLATE_SESSION_REAUTH_MESSAGE =
  'Your session is not usable — please sign in again and retry.';

export const getTemplateHttpStatus = (error: unknown): number | undefined => {
  if (error instanceof HttpError) return error.status;
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: number }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
};

export const getTemplateSessionGuidance = (error: unknown): string | undefined =>
  getTemplateHttpStatus(error) === 500 ? TEMPLATE_SESSION_REAUTH_MESSAGE : undefined;

/** A missing id, a deleted template, and someone else's template all return an identical 404. */
export const isTemplateNotFoundError = (error: unknown): boolean =>
  getTemplateHttpStatus(error) === 404;

export const isTemplateConflictError = (error: unknown): boolean =>
  getTemplateHttpStatus(error) === 409;

export const getTemplateFieldErrorsFromUnknown = (error: unknown): Record<string, string[]> => {
  if (!(error instanceof HttpError)) return {};
  const body = error.error as { errors?: string[] | Record<string, string[]> };
  return parseTemplateFieldErrors(body?.errors);
};

export const getTemplateErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof HttpError) {
    const body = error.error as { message?: string };
    if (typeof body?.message === 'string' && body.message.trim()) {
      return body.message;
    }
  }
  return error instanceof Error ? error.message : fallback;
};
